import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useMemo, useState } from "react";
import { PostTypeKey } from "../../types/common.type";
import { Episode } from "../../types/episode";

interface EpisodeListProps {
  episodes: Episode[];
  movieSlug: string;
  onPlayEpisode: (url: string) => void;
  currentVideoUrl: string;
  type?: PostTypeKey;
  viewMode?: 'grid' | 'list';
  isNext?: boolean;
  page?: number;
  onPageChange?: (page: number) => void;
}

const ITEMS_PER_PAGE = 20;

const EpisodeList: React.FC<EpisodeListProps> = ({ 
  episodes, 
  onPlayEpisode, 
  currentVideoUrl, 
  type = 'movie', 
  viewMode = 'grid',
  isNext,
  page = 1,
  onPageChange
}) => {
  const [search, setSearch] = useState("");

  // Lọc tập theo search nếu ở chế độ list
  const filteredEpisodes = useMemo(() => {
    if (viewMode !== 'list' || !search.trim()) return episodes;
    return episodes.filter(
      (ep) =>
        (ep.title || "").toLowerCase().includes(search.toLowerCase()) ||
        String(ep.index).includes(search)
    );
  }, [episodes, search, viewMode]);

  // Xử lý chuyển trang
  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center items-center mt-2 md:mt-4 space-x-1 md:space-x-2">
        {page > 1 && (
          <button
            onClick={() => handlePageChange(page - 1)}
            className="p-0.5 md:p-1 rounded bg-gray-700 text-white hover:bg-gray-600"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
        <span className="text-white px-2 md:px-3 text-xs md:text-sm">{`Trang ${page}`}</span>
        {isNext && (
          <button
            onClick={() => handlePageChange(page + 1)}
            className="p-0.5 md:p-1 rounded bg-gray-700 text-white hover:bg-gray-600"
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
      </div>
    );
  };

  const renderEpisodes = (episodes: Episode[] = []) => {
    if (viewMode === 'list') {
      return (
        <div className="flex flex-col gap-1 md:gap-2 mt-2 md:mt-3">
          {episodes.map((ep, idx) => {
            const isActive = type === 'movie'
              ? currentVideoUrl === ep.source[0]?.link
              : currentVideoUrl === String(ep.id);
            return (
              <div
                key={ep.id || idx}
                className={`w-full px-2 md:px-3 py-1 border rounded flex items-center cursor-pointer transition-all ${
                  isActive ? "bg-primary text-white font-bold" : "bg-gray-800 text-white hover:bg-gray-700"
                }`}
                onClick={() => {
                  onPlayEpisode(type === 'movie' ? (ep.source[0]?.link || "") : String(ep.index));
                }}
              >
                <span className="flex-1 text-xs md:text-sm text-left truncate">{ep.title || `Tập ${ep.index}`}</span>
              </div>
            );
          })}
        </div>
      );
    }
    
    return (
      <div className="flex flex-wrap gap-1 md:gap-2 mt-2 md:mt-4">
        {episodes.map((ep, idx) => {
          const isActive = type === 'movie'
            ? currentVideoUrl === ep.source[0]?.link
            : currentVideoUrl === String(ep.index);
          return (
            <div
              key={ep.index || idx}
              className={`w-8 md:w-10 py-1 border rounded items-center justify-center text-center cursor-pointer text-xs md:text-sm ${
                isActive
                  ? "bg-primary text-white"
                  : "bg-gray-700 text-white"
              }`}
              onClick={() => {
                onPlayEpisode(type === 'movie' ? (ep.source[0]?.link || "") : String(ep.index));
              }}
            >
              {`${ep.index|| ep.novel_index}`}
            </div>
          );
        })}
      </div>
    );
  };
 
  if (!episodes || episodes.length === 0 || episodes.length === 1) return null;

  return (
    <div className="chapter-list my-3 md:my-6 max-h-[calc(100vh-30vh)] overflow-y-auto p-2 md:p-4">
      <div className="">
        <span className="text-white text-base md:text-lg font-bold">Tập :</span>
        {viewMode === 'list' && (
          <input
            className="w-full mt-2 md:mt-3 mb-2 md:mb-4 px-2 md:px-3 py-1 md:py-2 rounded bg-gray-900 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            type="text"
            placeholder="Tìm kiếm tên chương/tập..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        )}
        {renderEpisodes(filteredEpisodes)}
        {(isNext || page > 1) && renderPagination()}
      </div>
    </div>
  );
};

export default EpisodeList;
